import { useState, useEffect } from "react";
import { Stack, DetailsList, DetailsListLayoutMode, IColumn, SelectionMode, Dialog, DialogType, DialogFooter, Spinner, SpinnerSize, DefaultButton, PrimaryButton } from "@fluentui/react";
import { getGroups, deleteGroup } from "../../../api/admin/api_admin";
import { GroupDetail, GroupsResponse } from "../../../api/admin/models_admin";
import { AdminLabel, LinkButton, CellContent, formatDateTime, ErrorDialog } from "../components";

export const Groups = () => {
    const [groupsData, setGroupsData] = useState<GroupsResponse | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [deleteDialogVisible, setDeleteDialogVisible] = useState<boolean>(false);
    const [groupToDelete, setGroupToDelete] = useState<GroupDetail | null>(null);
    const [deleting, setDeleting] = useState<boolean>(false);

    useEffect(() => {
        fetchGroups();
    }, []);

    const fetchGroups = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getGroups();
            if ("error" in data) {
                setError(data.error);
            } else {
                setGroupsData(data);
            }
        } catch (err) {
            setError("グループ情報の取得中にエラーが発生しました");
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (group: GroupDetail) => {
        setGroupToDelete(group);
        setDeleteDialogVisible(true);
    };

    const handleDeleteConfirm = async () => {
        if (!groupToDelete) return;
        
        setDeleting(true);
        try {
            const result = await deleteGroup(String(groupToDelete.GRP_group_cd));
            if (result.success) {
                setDeleteDialogVisible(false);
                setGroupToDelete(null);
                await fetchGroups(); // 再取得
            } else {
                setError(result.message || "削除に失敗しました");
                setDeleteDialogVisible(false);
            }
        } catch (err) {
            setError("削除中にエラーが発生しました");
            setDeleteDialogVisible(false);
        } finally {
            setDeleting(false);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogVisible(false);
        setGroupToDelete(null);
    };

    const canModify = (): boolean => {
        if (!groupsData) return false;
        const modifyFunc = groupsData.functions['modify_group'];
        return modifyFunc && 
               groupsData.current_user.authority_level >= modifyFunc.need_authority_level;
    };

    const canDelete = (): boolean => {
        if (!groupsData) return false;
        const deleteFunc = groupsData.functions['delete_group'];
        return deleteFunc && 
               groupsData.current_user.authority_level >= deleteFunc.need_authority_level;
    };

    const canAddGroup = (): boolean => {
        if (!groupsData) return false;
        const addFunc = groupsData.functions['add_group'];
        return addFunc && groupsData.current_user.authority_level >= addFunc.need_authority_level;
    };

    const actionWidth = 30 + (canModify() ? 60 : 0) + (canDelete() ? 60 : 0)
    const columns: IColumn[] = [
        {
            key: 'actions',
            name: '',
            minWidth: actionWidth,
            maxWidth: actionWidth,
            onRender: (group: GroupDetail) => (
                <Stack horizontal tokens={{ childrenGap: 8 }}>
                    {canModify() && (
                        <LinkButton 
                            link_to={`/menu/groups/modify?group_cd=${group.GRP_group_cd}`} 
                            text="変更" 
                            styles={{ root: { minWidth: 60 } }} 
                        />
                    )}
                    {canDelete() && (
                        <DefaultButton 
                            text="削除"
                            onClick={() => handleDeleteClick(group)}
                            styles={{ root: { minWidth: 60 } }}
                        />
                    )}
                </Stack>
            )
        },
        {
            key: 'group_cd',
            name: 'グループCD',
            fieldName: 'GRP_group_cd',
            minWidth: 100,
            maxWidth: 150,
            isResizable: true,
            onRender: (group: GroupDetail) => (
                <CellContent>{group.GRP_group_cd}</CellContent>
            )
        },
        {
            key: 'group_name',
            name: 'グループ名',
            fieldName: 'GRP_group_name',
            minWidth: 150,
            maxWidth: 250,
            isResizable: true,
            onRender: (group: GroupDetail) => (
                <CellContent>{group.GRP_group_name}</CellContent>
            )
        },
        {
            key: 'entry_datetime',
            name: '登録日時',
            fieldName: 'GRP_entry_datetime',
            minWidth: 150,
            maxWidth: 180,
            isResizable: true,
            onRender: (group: GroupDetail) => (
                <CellContent>{formatDateTime(group.GRP_entry_datetime)}</CellContent>
            )
        },
        {
            key: 'update_datetime',
            name: '更新日時',
            fieldName: 'GRP_update_datetime',
            minWidth: 150,
            maxWidth: 180,
            isResizable: true,
            onRender: (group: GroupDetail) => (
                <CellContent>{formatDateTime(group.GRP_update_datetime)}</CellContent>
            )
        },
        {
            key: 'rag_flag',
            name: '文書検索モード設定',
            minWidth: 120,
            maxWidth: 180,
            isResizable: true,
            onRender: (group: GroupDetail) => (
                <CellContent>
                    {group.GRP_default_setting_rag_flag ? '有効' : '-'}
                </CellContent>
            )
        },
        {
            key: 'default_model',
            name: 'デフォルトモデル',
            minWidth: 150,
            maxWidth: 200,
            isResizable: true,
            onRender: (group: GroupDetail) => {
                const content = group.GRP_default_setting_model && groupsData?.models[group.GRP_default_setting_model]
                    ? groupsData.models[group.GRP_default_setting_model]
                    : '-';
                return <CellContent>{content}</CellContent>;
            }
        }
    ];

    if (loading) {
        return (
            <Stack horizontalAlign="center" verticalAlign="center" style={{ minHeight: '400px' }}>
                <Spinner size={SpinnerSize.large} label="読み込み中..." />
            </Stack>
        );
    }

    return (
        <Stack tokens={{ childrenGap: 20 }}>
            <AdminLabel text="グループ一覧" />

            {
                <ErrorDialog 
                    content={error ?? ""} 
                    onDismiss={() => setError(null)} 
                    isOpen={Boolean(error)} />
            }

            <DetailsList
                items={groupsData?.groups || []}
                columns={columns}
                layoutMode={DetailsListLayoutMode.justified}
                selectionMode={SelectionMode.none}
                isHeaderVisible={true}
                onShouldVirtualize={() => false}
            />

            {canAddGroup() && (
                <Stack horizontal>
                    <LinkButton link_to="/menu/groups/add" text="新規登録" />
                </Stack>
            )}

            <Dialog
                hidden={!deleteDialogVisible}
                onDismiss={handleDeleteCancel}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: '削除確認',
                    subText: `グループ「${groupToDelete?.GRP_group_name}」を削除してもよろしいですか?`
                }}
            >
                <DialogFooter>
                    <PrimaryButton onClick={handleDeleteConfirm} text="削除" disabled={deleting} />
                    <DefaultButton onClick={handleDeleteCancel} text="キャンセル" disabled={deleting} />
                </DialogFooter>
            </Dialog>
        </Stack>
    );
}